import { isPaidSubscription, type SubscriptionLike } from '@/lib/quota/isPaidSubscription';
import { isCanceledSubscription } from './isCanceledSubscription';
import { isPartnerActive } from './isPartnerActive';
import { isSubscriptionPastDue } from './isSubscriptionPastDue';

export interface PlanDatesSubscriptionLike extends SubscriptionLike {
  past_due?: boolean | null;
  cancellation_at?: string | null;
  next_payment_at?: string | null;
  wallet_plan_until?: string | null;
  current_period_end?: string | null;
  is_partner?: boolean | null;
  partner_expires_at?: string | null;
  /** Backend: clips and time reset when the payment lands (monthly Stripe / PIX). */
  usage_resets_on_payment?: boolean | null;
  /** Backend: next scheduled reset (ISO instant), null when it waits for payment. */
  next_usage_reset_at?: string | null;
  /** Backend: a PIX renewal paid early keeps the current limits until this
   *  instant; it wins over the two fields above while it is set. */
  pending_cycle_reset_at?: string | null;
  /** Store subscriptions (`app_store` / `google_play`): the store's product id. */
  store_product_id?: string | null;
  store_environment?: string | null;
}

export type PlanDateKind = 'renews' | 'expires' | 'partner';

export interface PlanDates {
  /** Next scheduled clips/time reset (ISO instant) — not for payment-driven plans. */
  reset: string | null;
  /** When the plan renews, expires, or the partner grant ends: an ISO instant,
   *  or a calendar day (YYYY-MM-DD) for fields without a time (PIX due date). */
  plan: { kind: PlanDateKind; at: string } | null;
  /** Clips and time reset when the payment lands, i.e. on the plan date. */
  resetsOnPayment: boolean;
  /** Past due on a payment-driven plan: usage resets once the payment lands. */
  awaitingPayment: boolean;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Instants count until the moment passes; calendar days (UTC, the backend
// jobs' clock) until the day ends.
function isUpcoming(value: string | null | undefined, now: Date): value is string {
  if (!value) return false;
  if (DATE_ONLY.test(value)) return value >= now.toISOString().slice(0, 10);
  const time = new Date(value).getTime();
  return !Number.isNaN(time) && time > now.getTime();
}

const firstUpcoming = (values: Array<string | null | undefined>, now: Date): string | null => (
  values.find((value): value is string => isUpcoming(value, now)) ?? null
);

/**
 * What a plan card shows: when the plan renews (or expires / the partner grant
 * ends) and when clips and processing time reset. Monthly Stripe and PIX reset
 * when their payment lands — on the plan date itself; everything else resets
 * at the backend's next scheduled run (`next_usage_reset_at`). A PIX renewal
 * paid early resets on its old due date (`pending_cycle_reset_at`) instead,
 * shown for as long as the field is set: the hourly job applies it up to an
 * hour after the instant, and the old quota counts until then.
 *
 * Billing fields per provider: Stripe `current_period_end`, PIX
 * `next_payment_at`, grants `wallet_plan_until` (partner-API accounts keep
 * their period in `current_period_end`). Active partners ignore leftover
 * billing fields. Past moments are never shown, except a pending reset that is
 * still set.
 */
export function resolvePlanDates(
  subscription: PlanDatesSubscriptionLike | null | undefined,
  now: Date,
): PlanDates {
  if (!subscription) return { reset: null, plan: null, resetsOnPayment: false, awaitingPayment: false };

  const deferredReset = subscription.pending_cycle_reset_at || null;
  const resetsOnPayment = !deferredReset && Boolean(subscription.usage_resets_on_payment);
  const nextReset = subscription.next_usage_reset_at;
  const isScheduledInstant = isUpcoming(nextReset, now) && !DATE_ONLY.test(nextReset);
  const reset = deferredReset ?? (!resetsOnPayment && isScheduledInstant ? nextReset : null);
  const pastDue = isSubscriptionPastDue(subscription);
  const result = (kind: PlanDateKind, at: string | null): PlanDates => ({
    reset,
    plan: at ? { kind, at } : null,
    resetsOnPayment,
    awaitingPayment: resetsOnPayment && pastDue,
  });

  if (!isPaidSubscription(subscription)) return result('renews', null);

  const partnerExpiresAt = subscription.partner_expires_at ?? null;
  if (isPartnerActive({ isPartner: Boolean(subscription.is_partner), expiresAt: partnerExpiresAt }, now)) {
    return result('partner', firstUpcoming([partnerExpiresAt], now));
  }

  if (pastDue) return result('renews', null);

  if (isCanceledSubscription(subscription)) {
    return result('expires', firstUpcoming([subscription.cancellation_at], now));
  }

  const isStripe = subscription.payment_provider === 'stripe'
    || Boolean(subscription.stripe_subscription_id);
  let candidates: Array<string | null | undefined>;
  if (isStripe) {
    candidates = [subscription.current_period_end, subscription.next_payment_at];
  } else if (subscription.payment_provider === 'pagarme') {
    candidates = [subscription.next_payment_at];
  } else {
    candidates = [
      subscription.wallet_plan_until,
      subscription.next_payment_at,
      subscription.current_period_end,
    ];
  }
  return result('renews', firstUpcoming(candidates, now));
}
